import { ButtonHTMLAttributes } from "react";
import { LtIcons } from "@landtechnologies/icons/font/lt-icons";
import clsx from "clsx";

export interface NavbarItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  anchorActive?: boolean;
  leadIcon?: `${LtIcons}`;
  leadIconProps?: Omit<NavbarItemIconProps, "icon">;
  tailIcon?: `${LtIcons}`;
  mobile?: boolean;
}

export const NavbarItem = ({
  active,
  anchorActive,
  children,
  leadIcon,
  tailIcon,
  mobile,
  ...rest
}: NavbarItemProps) => {
  if (!mobile) {
    return (
      <NavbarItemDesktop active={active} leadIcon={leadIcon} tailIcon={tailIcon} anchorActive={anchorActive} {...rest}>
        {children}
      </NavbarItemDesktop>
    );
  }
  return (
    <NavbarItemMobile active={active} leadIcon={leadIcon} {...rest}>
      {children}
    </NavbarItemMobile>
  );
};

export const NavbarItemDesktop = ({
  active,
  children,
  leadIcon,
  leadIconProps = {},
  tailIcon,
  anchorActive,
  ...rest
}: NavbarItemProps) => {
  return (
    <button
      {...rest}
      className={clsx(
        "relative group h-full items-center px-4 hover:bg-blue-grey-300 hidden md:flex cursor-pointer",
        {
          "text-blue-grey-950": !active,
          "text-platform-blue-700": active,
          "bg-blue-grey-300": anchorActive,
        },
        rest.className
      )}
      aria-current={active}
    >
      {active && <Line />}
      {leadIcon && <NavbarItemIcon icon={leadIcon} {...leadIconProps} />}
      {children && <span className="hidden p-1 md:block">{children}</span>}
      {tailIcon && <NavbarItemIcon icon={tailIcon} />}
    </button>
  );
};

export const NavbarItemMobile = ({ active, children, leadIcon, leadIconProps = {}, ...rest }: NavbarItemProps) => {
  return (
    <button
      {...rest}
      className={clsx("flex flex-col items-center w-full", active && "text-text-base-active")}
      aria-current={active}
    >
      {leadIcon && <NavbarItemIcon icon={leadIcon} {...leadIconProps} />}
      <span className="text-xs">{children}</span>
    </button>
  );
};

interface NavbarItemIconProps {
  icon: `${LtIcons}`;
  alwaysShow?: boolean;
  noPaddings?: boolean;
}

function NavbarItemIcon({ icon, alwaysShow = false, noPaddings = false }: NavbarItemIconProps) {
  return <i className={clsx("block text-xl", `icon-${icon}`, !alwaysShow && "md:hidden", !noPaddings && "pt-2")} />;
}

function Line() {
  return (
    <div className="absolute bottom-0 w-[calc(100%-1rem)] h-1 rounded-t-lg left-1/2 transform -translate-x-1/2 ease-in-out duration-100 bg-platform-blue-700" />
  );
}
